import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { LocalizedDatePipe } from '@core/pipes/localize-date.pipe';
import { environment } from '@env';
import { TranslocoDirective } from '@jsverse/transloco';
import type { Language } from '@kerberos-compliance/compliance-shared-ts/common/i18n/enums/language.enums';
import type { ResponseDeliveryTrackerCacheDto } from '@kerberos-compliance/compliance-shared-ts/features/delivery/dtos/response.delivery-tracker.cache-dto';
import type { DeliveryTrackerReminder } from '@kerberos-compliance/compliance-shared-ts/features/delivery/enums/delivery-tracker-reminder.enum';
import { DeliveryTrackerReminderMap } from '@kerberos-compliance/compliance-shared-ts/features/delivery/enums/delivery-tracker-reminder.enum';
import type { DeliveryTrackerStatus } from '@kerberos-compliance/compliance-shared-ts/features/delivery/enums/delivery-tracker-status.enum';
import { DeliveryTrackerStatusMap } from '@kerberos-compliance/compliance-shared-ts/features/delivery/enums/delivery-tracker-status.enum';
import { TranslationsDeliveryTrackerReminder } from '@kerberos-compliance/compliance-shared-ts/features/delivery/i18n/delivery-tracker-reminder.translation';
import { TranslationsDeliveryTrackerStatus } from '@kerberos-compliance/compliance-shared-ts/features/delivery/i18n/delivery-tracker-status.translation';
import { IconComponent, IconNameMap, IconSizeMap, TranslationService } from '@kerberos-compliance/compliance-shared-ui';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';

type DeliveryTrackerStatusStepMap = {
  statusTranslation: string;
  statusValue: DeliveryTrackerStatus;
  stepNumber: number;
  tooltip: string;
};

@Component({
  selector: 'aad-delivery-tracker-status',
  imports: [NgTemplateOutlet, NzStepsModule, NzTooltipDirective, IconComponent, LocalizedDatePipe, TranslocoDirective],
  templateUrl: 'delivery-tracker-status.component.html',
  styleUrl: 'delivery-tracker-status.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryTrackerStatusComponent {
  private readonly translationService = inject(TranslationService);

  public readonly deliveryTracker = input.required<ResponseDeliveryTrackerCacheDto>();

  protected readonly currentStepNumber = computed(() => {
    const currentStep = this.deliveryTrackerSteps().find((step) => step.statusValue === this.deliveryTracker().status);
    return currentStep ? currentStep.stepNumber : 0;
  });

  protected readonly deliveryTrackerSteps = computed(() =>
    this.deliveryTrackerStatusSteps(this.translationService.activeLanguage()),
  );

  protected readonly reminder = computed(() =>
    this.getReminderTranslation(this.deliveryTracker().reminder, this.translationService.activeLanguage()),
  );

  protected readonly isEscalated = computed(
    () => this.deliveryTracker().reminder === DeliveryTrackerReminderMap.Escalation,
  );

  protected readonly showSharepointLink = computed(
    () =>
      this.deliveryTracker().status === DeliveryTrackerStatusMap.Pending ||
      this.deliveryTracker().status === DeliveryTrackerStatusMap.Complete,
  );

  protected readonly sharepointUrl = computed(
    () => `${environment.sharepointRiskAnalysisUrl}/${this.deliveryTracker().organizationId}`,
  );

  /**
   * Builds the list of delivery tracker status steps for the stepper component.
   * Excludes Draft and InactiveContract statuses and sorts by status value ascending.
   *
   * @param {Language} activeLanguage - The active language used to translate status labels
   * @returns {DeliveryTrackerStatusStepMap[]} An array of status step configurations for the stepper
   */
  private deliveryTrackerStatusSteps(activeLanguage: Language): DeliveryTrackerStatusStepMap[] {
    return Object.entries(DeliveryTrackerStatusMap)
      .filter(
        ([key, value]) =>
          value !== DeliveryTrackerStatusMap.Draft && value !== DeliveryTrackerStatusMap.InactiveContract,
      )
      .map(([key, value], index) => ({
        statusValue: value,
        statusTranslation: this.getStatusTranslation(value, activeLanguage),
        stepNumber: index,
        tooltip: this.getStatusTooltip(key),
      }))
      .toSorted((a, b) => a.statusValue - b.statusValue);
  }

  /**
   * Retrieves the translated label for a given delivery tracker status.
   *
   * @param {DeliveryTrackerStatus} statusKey - The delivery tracker status to translate
   * @param {Language} activeLanguage - The active language for translation lookup
   * @returns {string} The translated status label
   */
  private getStatusTranslation(statusKey: DeliveryTrackerStatus, activeLanguage: Language): string {
    return TranslationsDeliveryTrackerStatus[activeLanguage][statusKey];
  }

  /**
   * Retrieves the tooltip translation for a given delivery tracker status key.
   *
   * @param {string} statusKey - The status key used to build the translation path
   * @returns {string} The translated tooltip text for the status
   */
  private getStatusTooltip(statusKey: string): string {
    const translationKey = `organizations.detailPage.riskAnalysisDeliveryTab.stepperTooltips.${statusKey.toLowerCase()}`;
    return this.translationService.getTranslation(translationKey);
  }

  /**
   * Retrieves the translated label for a given delivery tracker reminder type.
   *
   * @param {DeliveryTrackerReminder} reminder - The reminder type to translate
   * @param {Language} activeLanguage - The active language for translation lookup
   * @returns {string} The translated reminder label
   */
  private getReminderTranslation(reminder: DeliveryTrackerReminder, activeLanguage: Language): string {
    return TranslationsDeliveryTrackerReminder[activeLanguage][reminder];
  }

  protected readonly IconNameMap = IconNameMap;

  protected readonly IconSizeMap = IconSizeMap;
}
